import { isApiError, isTechnicalError } from '@multaqa/api-client'
import { i18next } from '@multaqa/i18n'

const GENERIC_API_MESSAGES = new Set([
  'Internal Server Error',
  'Request failed',
  'Server Error',
  'An unexpected error occurred. Please try again or contact support.',
  'Request failed with status code 500',
])

function resolveTranslatedTitle(key: string, defaultValue?: string): string {
  const titleKey = `${key}.title`
  if (i18next.exists(titleKey)) {
    const val = i18next.t(titleKey)
    if (typeof val === 'string') return val
  }
  if (i18next.exists(key)) {
    const val = i18next.t(key)
    if (typeof val === 'string') return val
  }
  return defaultValue ?? i18next.t('error.generic.title', { defaultValue: 'Something went wrong' })
}

/**
 * Format any error (ApiError, Error, or unknown) into a user-friendly, localized message.
 * Ensures raw SQL statements, database constraint errors, stack traces, and internal server exceptions
 * are never displayed to end users.
 */
export function formatErrorMessage(error: unknown, fallback?: string): string {
  if (isApiError(error)) {
    // 5xx Server Error or technical SQL / exception dump
    if (error.kind === 'server' || error.status >= 500 || isTechnicalError(error.message)) {
      const genericTitle = fallback ?? resolveTranslatedTitle('error.generic')
      if (error.reference) {
        const refLabel = i18next.t('error.reference', { defaultValue: 'Reference' })
        return `${genericTitle} (${refLabel}: ${error.reference})`
      }
      return genericTitle
    }

    if (error.kind === 'network' || error.status === 0) {
      return resolveTranslatedTitle('error.network')
    }

    if (error.kind === 'unauthorized') {
      return resolveTranslatedTitle('error.unauthorized')
    }

    if (error.kind === 'forbidden') {
      if (error.code === 'module_not_enabled') {
        return resolveTranslatedTitle('error.moduleNotEnabled')
      }
      if (error.code === 'group_suspended') {
        return resolveTranslatedTitle('error.suspended')
      }
      if (
        error.message &&
        !isTechnicalError(error.message) &&
        !GENERIC_API_MESSAGES.has(error.message)
      ) {
        return error.message
      }
      return resolveTranslatedTitle('error.forbidden')
    }

    if (error.kind === 'notFound') {
      return resolveTranslatedTitle('error.notFound')
    }

    if (error.kind === 'rateLimited') {
      return resolveTranslatedTitle('error.rateLimited')
    }

    if (error.kind === 'validation') {
      if (error.errors) {
        const firstEntry = Object.values(error.errors)[0]
        if (firstEntry?.[0]) {
          return firstEntry[0]
        }
      }
      if (
        error.message &&
        !isTechnicalError(error.message) &&
        !GENERIC_API_MESSAGES.has(error.message)
      ) {
        return error.message
      }
      return resolveTranslatedTitle('error.validation')
    }

    if (error.kind === 'conflict') {
      if (
        error.message &&
        !isTechnicalError(error.message) &&
        !GENERIC_API_MESSAGES.has(error.message)
      ) {
        return error.message
      }
      return resolveTranslatedTitle('error.conflict')
    }

    if (
      error.message &&
      !isTechnicalError(error.message) &&
      !GENERIC_API_MESSAGES.has(error.message)
    ) {
      return error.message
    }

    return resolveTranslatedTitle(error.translationKey, fallback)
  }

  return fallback ?? resolveTranslatedTitle('error.generic')
}
